import cardResource from "@/shared/resource/card";
import { authorizedAxios } from "./authorizedAxios";
import {
  DependentRequest,
  FiservDebitCardListResponse,
  CardListWexResponse,
  SetCardPinRequest,
  CardProviderOptionsResponse,
  FeePackageListResponse,
  DebitCardListResponse,
  OrderReplacementCardRequest,
  CardAddDependentResponse,
  PurseStackingOrdersListResponse
} from "@/shared/types";
import {
  CardIssueConfigResponse,
  CardEmployerList,
  IssueCardCustomizationResponse,
  EmployerCardIssueCriteriaResponse,
  CardIssueCustomizationOptionsResponse,
  AccountHolderIssueCardCriteriaResponse,
  SetCardCriteriaCustomizationRequest,
  CreateCardIssueCriteriaRequest,
  CardIssueCustomizationOptionUpdateRequest,
  CardCustomizationBulkImportFileResponse,
  CardCustomizationBulkImportResultsResponse,
  EmployerPrinSettingsResponse,
  CardEmployerPrinSettingsRequest,
  PartnerIndependentCardManagement
} from "@/admin/types";
import axios from "axios";

export function getFiservDebitCards () {
  return authorizedAxios.get<FiservDebitCardListResponse>(cardResource.fiservDebitCards());
}
export function getDebitCards (args?: { username: string, partnerId: number }) {
  const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
  return authorizedAxios.get<DebitCardListResponse>(cardResource.debitCards(), params);
}
export function setDebitCardPin (cardId: number, setCardPinRequest: SetCardPinRequest) {
  return authorizedAxios.post(cardResource.setDebitCardPin(cardId), setCardPinRequest);
}
export function orderPrimaryCard () {
  return authorizedAxios.put(cardResource.orderPrimaryCard());
}
export function orderPrimaryDuplicateCard () {
  return authorizedAxios.post(cardResource.orderPrimaryDuplicateCard());
}
export function orderDuplicateCard (cardId: number) {
  return authorizedAxios.post(cardResource.orderDuplicateCard(cardId));
}
export function orderReplacementCard (cardId: number, orderReplacementCardRequest: OrderReplacementCardRequest) {
  return authorizedAxios.post(cardResource.orderReplacementCard(cardId), orderReplacementCardRequest);
}
export function addFiservDebitCardDependent (dependentRequest: DependentRequest) {
  return authorizedAxios.post<CardAddDependentResponse>(cardResource.addFiservDebitCardDependent(), dependentRequest);
}
export function deativateFiservDebitCard (fiservUserInfoId: number) {
  return authorizedAxios.post(cardResource.deativateFiservDebitCard(fiservUserInfoId));
}
export function replaceFiservDebitCard (fiservUserInfoId: number) {
  return authorizedAxios.post(cardResource.replaceFiservDebitCard(fiservUserInfoId));
}
export function confirmReplacementFiservDebitCard (fiservUserInfoId: number) {
  return authorizedAxios.post(cardResource.confirmReplacementFiservDebitCard(fiservUserInfoId));
}
export function getPartnerIssueCriterias (partnerId: number) {
  return authorizedAxios.get<CardIssueConfigResponse>(cardResource.partnerCardIssueCriterias(partnerId));
}
export function getEmployerIssueCriteria (employerId: number) {
  return authorizedAxios.get<EmployerCardIssueCriteriaResponse>(cardResource.employerCardIssueCriteria(employerId));
}
export function getAccountHoldersIssueCardCriterias (employerId: number) {
  return authorizedAxios.get<AccountHolderIssueCardCriteriaResponse>(cardResource.accountHoldersIssueCardCriterias(employerId));
}
export function getCardIssueCustomization (issueCardCriteriaId: number) {
  return authorizedAxios.get<IssueCardCustomizationResponse>(cardResource.issueCardCustomization(issueCardCriteriaId));
}
export function deleteCardIssueCriterias (id: number) {
  return authorizedAxios.delete(cardResource.deleteCardIssueCriterias(id));
}
export function createCardIssueCriteria (request: CreateCardIssueCriteriaRequest) {
  return authorizedAxios.post(cardResource.createCardIssueCriteria(), request);
}

export function activateCardIssueCriterias (id: number) {
  return authorizedAxios.post(cardResource.activateCardIssueCriterias(id));
}

export function setCardCriteriaCustomization (issueCardCriteriaId: number, request: SetCardCriteriaCustomizationRequest) {
  return authorizedAxios.put<void>(cardResource.setCardCriteriaCustomization(issueCardCriteriaId), request);
}

export function getEmployersForPartner (partnerId: number) {
  return authorizedAxios.get<CardEmployerList>(cardResource.getEmployersForPartner(partnerId));
}

export function getUnconfiguredEmployers (partnerId: number) {
    return authorizedAxios.get<CardEmployerList>(cardResource.getUnconfiguredEmployers(partnerId));
}

export function getPartnerIndependentCardMangement (partnerId: number) {
  return authorizedAxios.get<PartnerIndependentCardManagement>(cardResource.partnerIndependentCardManagement(partnerId));
}

export function setPartnerIndependentCardMangement (
  partnerId: number,
  request: PartnerIndependentCardManagement) {
  return authorizedAxios.put<PartnerIndependentCardManagement>(
    cardResource.partnerIndependentCardManagement(partnerId),
    request);
}

export function dismissFiservDebitCardPendingDependent (fiservAuthorizedSignerPendingId: number) {
  return authorizedAxios.post(cardResource.dismissPendingDependentFiservDebitCard(fiservAuthorizedSignerPendingId));
}

export function getWexCardList () {
  return authorizedAxios.get<CardListWexResponse>(cardResource.getWexCardList());
}

export function getCardProviderOptions (args?: { username: string, partnerId: number }) {
  const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
  return authorizedAxios.get<CardProviderOptionsResponse>(cardResource.getCardProviderOptions(), params);
}

export function getCardIssueCustomizationOptions () {
  return authorizedAxios.get<CardIssueCustomizationOptionsResponse>(cardResource.cardIssueCustomizationOptions());
}

export function updateCardIssueCustomizationOptions (request: CardIssueCustomizationOptionUpdateRequest) {
  return authorizedAxios.put<CardIssueCustomizationOptionsResponse>(cardResource.cardIssueCustomizationOptions(), request);
}

export function listFeePackages () {
  return authorizedAxios.get<FeePackageListResponse>(cardResource.feePackages());
}

export function getCardCustomizationBulkImportFiles () {
  return authorizedAxios.get<CardCustomizationBulkImportFileResponse>(cardResource.cardCustomizationBulkImportFiles());
}

export function listPurseStackingOrders () {
  return authorizedAxios.get<PurseStackingOrdersListResponse>(cardResource.purseStackingOrdersList());
}

export function getCardCustomizationBulkImportResultsFileUrl (fileId: number) {
  return authorizedAxios.get<CardCustomizationBulkImportResultsResponse>(cardResource.cardCustomizationBulkImportResultsFileUrl(fileId));
}

export function uploadCardCustomizationBulkImportFile (file: File) {
  return authorizedAxios.postFiles(cardResource.cardCustomizationBulkImportFiles(), [ file ]);
}

export function getEmployerPrinSettings (employerId: number) {
  return authorizedAxios.get<EmployerPrinSettingsResponse>(cardResource.getCardEmployerPrinSettings(employerId));
}

export function saveCardEmployerPrinSettings (request: CardEmployerPrinSettingsRequest) {
  return authorizedAxios.post(cardResource.setCardEmployerPrinSettings(), request);
}